import type {
  AdjudicationStatus,
  BackgroundCheckAssessment,
  BackgroundCheckProgressiveState,
} from '@traba/types'
import {
  BackgroundCheckStatus,
  DetailedBackgroundCheckStatus,
} from '@traba/types'
import type {
  BgcActiveActionInfo,
  BgcBadgeConfig,
  BgcBadgeVariant,
  BgcRawFields,
} from '../types/bgcDisplayUtils.types'

export type {
  BgcActiveActionInfo,
  BgcBadgeConfig,
  BgcBadgeVariant,
  BgcRawFields,
}

export function getBgcBadgeConfig(
  raw: BgcRawFields | null | undefined,
  activeAction: BgcActiveActionInfo | null | undefined,
): BgcBadgeConfig {
  if (activeAction) {
    return {
      variant: 'danger',
      title: activeAction.actionType === 'BLOCKED_ALL' ? 'Locked' : 'Pushed',
    }
  }
  if (!raw || !raw.status) {
    return { variant: 'danger', title: 'Not Started' }
  }
  const detailed = calculateDetailedBackgroundCheckStatus(
    raw.status as BackgroundCheckStatus,
    (raw.adjudication ?? undefined) as AdjudicationStatus | undefined,
    (raw.assessment ?? undefined) as BackgroundCheckAssessment | undefined,
    (raw.progressiveState ?? undefined) as
      | BackgroundCheckProgressiveState
      | null
      | undefined,
  )
  return (
    BGC_STATUS_BADGE_CONFIG[detailed] ?? { variant: 'info', title: 'Unknown' }
  )
}

export function isBgcPushable(
  raw: BgcRawFields | null | undefined,
  activeAction: BgcActiveActionInfo | null | undefined,
): boolean {
  if (activeAction) {
    return false
  }
  if (!raw || !raw.status) {
    return true
  }
  const detailed = calculateDetailedBackgroundCheckStatus(
    raw.status as BackgroundCheckStatus,
    (raw.adjudication ?? undefined) as AdjudicationStatus | undefined,
    (raw.assessment ?? undefined) as BackgroundCheckAssessment | undefined,
    (raw.progressiveState ?? undefined) as
      | BackgroundCheckProgressiveState
      | null
      | undefined,
  )
  return (
    detailed === DetailedBackgroundCheckStatus.PENDING ||
    detailed === DetailedBackgroundCheckStatus.INVITATION_CREATED
  )
}

export function calculateDetailedBackgroundCheckStatus(
  status: BackgroundCheckStatus,
  adjudication?: AdjudicationStatus,
  assessment?: BackgroundCheckAssessment,
  progressiveState?: BackgroundCheckProgressiveState | null,
): DetailedBackgroundCheckStatus {
  if (
    status !== BackgroundCheckStatus.CONSIDER &&
    status !== BackgroundCheckStatus.COMPLETE &&
    !progressiveState
  ) {
    return convertToDetailedStatus(status)
  }

  if (adjudication) {
    switch (adjudication) {
      case 'PRE_ADVERSE_ACTION':
        return DetailedBackgroundCheckStatus.PRE_ADVERSE_ACTION
      case 'POST_ADVERSE_ACTION':
        return DetailedBackgroundCheckStatus.POST_ADVERSE_ACTION
      case 'ENGAGED':
        if (assessment) {
          switch (assessment) {
            case 'ELIGIBLE':
              return DetailedBackgroundCheckStatus.ELIGIBLE
            case 'REVIEW':
              return DetailedBackgroundCheckStatus.ELIGIBLE_AFTER_REVIEW
            case 'ESCALATED':
              return DetailedBackgroundCheckStatus.ESCALATED
          }
        }
    }
  }

  if (assessment) {
    switch (assessment) {
      case 'ELIGIBLE':
        return DetailedBackgroundCheckStatus.ELIGIBLE
      case 'ESCALATED':
        return DetailedBackgroundCheckStatus.ESCALATED
      case 'REVIEW':
        return DetailedBackgroundCheckStatus.REVIEW
    }
  }

  return convertToDetailedStatus(status)
}

function convertToDetailedStatus(
  status: BackgroundCheckStatus,
): DetailedBackgroundCheckStatus {
  const mapping: Partial<
    Record<BackgroundCheckStatus, DetailedBackgroundCheckStatus>
  > = {
    [BackgroundCheckStatus.PENDING]: DetailedBackgroundCheckStatus.PENDING,
    [BackgroundCheckStatus.INVITATION_CREATED]:
      DetailedBackgroundCheckStatus.INVITATION_CREATED,
    [BackgroundCheckStatus.REPORT_CREATED]:
      DetailedBackgroundCheckStatus.REPORT_CREATED,
    [BackgroundCheckStatus.CONSIDER]: DetailedBackgroundCheckStatus.CONSIDER,
    [BackgroundCheckStatus.CLEAR]: DetailedBackgroundCheckStatus.CLEAR,
    [BackgroundCheckStatus.SUSPENDED]: DetailedBackgroundCheckStatus.SUSPENDED,
    [BackgroundCheckStatus.DISPUTE]: DetailedBackgroundCheckStatus.DISPUTE,
    [BackgroundCheckStatus.CANCELED]: DetailedBackgroundCheckStatus.CANCELED,
    [BackgroundCheckStatus.COMPLETE]: DetailedBackgroundCheckStatus.COMPLETE,
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return mapping[status]!
}

const BGC_STATUS_BADGE_CONFIG: Record<
  DetailedBackgroundCheckStatus,
  BgcBadgeConfig
> = {
  [DetailedBackgroundCheckStatus.PENDING]: {
    variant: 'danger',
    title: 'Not Started',
  },
  [DetailedBackgroundCheckStatus.SUSPENDED]: {
    variant: 'danger',
    title: 'Suspended',
  },
  [DetailedBackgroundCheckStatus.CANCELED]: {
    variant: 'danger',
    title: 'Canceled',
  },
  [DetailedBackgroundCheckStatus.INVITATION_CREATED]: {
    variant: 'danger',
    title: 'Not Started',
  },
  [DetailedBackgroundCheckStatus.DISPUTE]: {
    variant: 'warning',
    title: 'Dispute',
  },
  [DetailedBackgroundCheckStatus.CONSIDER]: {
    variant: 'warning',
    title: 'Consider',
  },
  [DetailedBackgroundCheckStatus.REPORT_CREATED]: {
    variant: 'info',
    title: 'Started',
  },
  [DetailedBackgroundCheckStatus.CLEAR]: {
    variant: 'success',
    title: 'Clear',
  },
  [DetailedBackgroundCheckStatus.COMPLETE]: {
    variant: 'success',
    title: 'Complete',
  },
  [DetailedBackgroundCheckStatus.POST_ADVERSE_ACTION]: {
    variant: 'danger',
    title: 'Post-adverse action',
  },
  [DetailedBackgroundCheckStatus.PRE_ADVERSE_ACTION]: {
    variant: 'warning',
    title: 'Pre-adverse action',
  },
  [DetailedBackgroundCheckStatus.ELIGIBLE]: {
    variant: 'success',
    title: 'Eligible',
  },
  [DetailedBackgroundCheckStatus.REVIEW]: {
    variant: 'warning',
    title: 'Review',
  },
  [DetailedBackgroundCheckStatus.ESCALATED]: {
    variant: 'danger',
    title: 'Escalated',
  },
  [DetailedBackgroundCheckStatus.ELIGIBLE_AFTER_REVIEW]: {
    variant: 'success',
    title: 'Eligible After Review',
  },
}
